import { jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from "react";
import { OktaAuth } from "@okta/okta-auth-js";
import { useQuery } from "@tanstack/react-query";
import { AuthenticationContext, CLI_SCOPES, SCOPES } from "./AuthenticationContext.js";
import { gql, useQuery as index_js_useQuery } from "../graphql/index.js";
import { NotAuthenticatedError } from "../errors/index.js";
import { GetCurrentUserContext } from "../generated/graphql.js";
function getRedirectUri(pkceEnabled, authRoutes) {
    return pkceEnabled ? `${window.location.origin}${authRoutes.LOGIN_PKCE_CALLBACK}` : `${window.location.origin}${authRoutes.LOGIN_CALLBACK}`;
}
function getRedirectPath(authRoutes) {
    return `${authRoutes.LOGIN_CALLBACK}`;
}
function getRedirectPkcePath(authRoutes) {
    return `${authRoutes.LOGIN_PKCE_CALLBACK}`;
}
function getImpersonateRedirectUri(pkceEnabled, authRoutes) {
    return pkceEnabled ? `${window.location.origin}${authRoutes.IMPERSONATE_PKCE_CALLBACK}` : `${window.location.origin}${authRoutes.IMPERSONATE_CALLBACK}`;
}
const POST_LOGIN_REDIRECT_URL_PARAM = 'redirectTo';
function getLoginPageLocation(loginRoute) {
    const current = globalThis.location;
    const redirectTo = current.pathname + current.search + current.hash;
    const params = new URLSearchParams();
    params.set(POST_LOGIN_REDIRECT_URL_PARAM, redirectTo);
    return {
        pathname: loginRoute,
        search: `?${params.toString()}`
    };
}
const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      isAdmin
    }
  }
`;
const CONFIG_RETRY_ATTEMPTS = 3;
function AuthenticationContextProvider(props) {
    const { children, fetchFn, loginRoute, authRoutes } = props;
    const configRequest = useQuery({
        queryKey: [
            'authConfigQuery'
        ],
        queryFn: async ()=>{
            const response = await fetchFn('/auth/config');
            return await response.json();
        },
        retry: CONFIG_RETRY_ATTEMPTS
    });
    const impersonateConfigRequest = useQuery({
        queryKey: [
            'authImpersonateConfigQuery'
        ],
        queryFn: async ()=>{
            const response = await fetchFn('/auth/impersonate/config');
            return await response.json();
        },
        retry: CONFIG_RETRY_ATTEMPTS
    });
    const configState = useRef({});
    const impersonateConfigState = useRef({});
    if (configState.current.config !== configRequest.data) {
        const config = configRequest.data;
        let oktaClient;
        let oktaCliClient;
        if (config?.type === 'okta') {
            oktaClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: getRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
                scopes: SCOPES,
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: config?.enable_pkce_auth_flow,
                tokenManager: {
                    autoRenew: false
                }
            });
            oktaCliClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: `${window.location.origin}${authRoutes.CLI_LOGIN_CALLBACK}`,
                scopes: CLI_SCOPES,
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: false,
                tokenManager: {
                    autoRenew: false
                }
            });
        }
        configState.current = {
            config,
            oktaClient,
            oktaCliClient
        };
    }
    if (!impersonateConfigRequest.error && impersonateConfigState.current.config !== impersonateConfigRequest.data) {
        const config = impersonateConfigRequest.data;
        let oktaClient;
        let oktaCliClient;
        if (config?.type === 'okta') {
            oktaClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: getImpersonateRedirectUri(config?.enable_pkce_auth_flow, authRoutes),
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: config?.enable_pkce_auth_flow,
                tokenManager: {
                    autoRenew: false
                }
            });
            oktaCliClient = new OktaAuth({
                clientId: config.okta.client_id,
                issuer: config.okta.issuer,
                redirectUri: `${window.location.origin}${authRoutes.CLI_IMPERSONATE_CALLBACK}`,
                postLogoutRedirectUri: `${window.location.origin}${authRoutes.LOGOUT_CALLBACK}`,
                pkce: false,
                tokenManager: {
                    autoRenew: false
                }
            });
        }
        impersonateConfigState.current = {
            config,
            oktaClient,
            oktaCliClient
        };
    }
    const [{ fetching: loading, error, data }, refreshCurrentUser] = index_js_useQuery({
        query: GET_CURRENT_USER,
        context: GetCurrentUserContext,
        requestPolicy: 'network-only'
    });
    let currentUser;
    if (void 0 !== error && error.networkError instanceof NotAuthenticatedError === false) throw error;
    currentUser = data?.me;
    const refreshAuthToken = useCallback(async ()=>{
        const pkceEnabled = configState.current.config?.enable_pkce_auth_flow || impersonateConfigState.current.config?.enable_pkce_auth_flow;
        const redirectPath = pkceEnabled ? getRedirectPkcePath(authRoutes) : getRedirectPath(authRoutes);
        const response = await fetchFn('/auth/refresh', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                redirect_path: redirectPath
            })
        });
        if (!response.ok) throw new Error('Failed to refresh auth token');
    }, [
        authRoutes,
        fetchFn
    ]);
    const authContext = useMemo(()=>({
            loginRoute,
            isLoading: loading || configRequest.isFetching || impersonateConfigRequest.isFetching,
            config: configState.current.config,
            oktaClient: configState.current.oktaClient,
            oktaImpersonateClient: impersonateConfigState.current.oktaClient,
            oktaCliClient: configState.current.oktaCliClient,
            oktaCliImpersonateClient: impersonateConfigState.current.oktaCliClient,
            oktaImpersonateConfig: impersonateConfigState.current.config,
            pkceEnabled: impersonateConfigState.current.config?.enable_pkce_auth_flow || configState.current.config?.enable_pkce_auth_flow,
            currentUser,
            refreshCurrentUser,
            refreshAuthToken
        }), [
        loading,
        configRequest,
        impersonateConfigRequest,
        currentUser,
        refreshCurrentUser,
        loginRoute,
        refreshAuthToken
    ]);
    return /*#__PURE__*/ jsx(AuthenticationContext.Provider, {
        value: authContext,
        children: children
    });
}
export { AuthenticationContextProvider, GET_CURRENT_USER, POST_LOGIN_REDIRECT_URL_PARAM, getLoginPageLocation };

//# sourceMappingURL=AuthenticationContextProvider.js.map